import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { CategoryData } from "../types/dashboardTypes";
import { translateDashboardDistributionLabel } from "../utils/dashboardI18n";

interface ComparisonBarChartProps {
  title?: string;
  data: CategoryData[];
  /** Shown when data is empty */
  emptyMessage?: string;
}

const PALETTE = [
  "#1976d2",
  "#2e7d32",
  "#f9a825",
  "#c62828",
  "#7b1fa2",
  "#00838f",
  "#6d4c41"
];

const getBarColors = (labels: string[]): string[] =>
  labels.map((l, i) => {
    const semantic: Record<string, string> = {
      "IN STOCK": "#2e7d32",
      "OUT OF STOCK": "#c62828",
      SOLD: "#757575",
      /** Title case from stringFormatter(enum) */
      Sold: "#e65100",
      Reserved: "#f9a825",
      Created: "#1565c0",
      Damaged: "#c62828",
      Expired: "#6a1b9a",
      Lost: "#5d4037",
      Consumed: "#00838f",
      Deleted: "#9e9e9e",
      "In Stock": "#2e7d32",
      "Out Of Stock": "#c62828",
      ACTIVE: "#2e7d32",
      INACTIVE: "#c62828",
      DELETED: "#9e9e9e",
      PENDING: "#f9a825",
      DRAFT: "#607d8b",
      "OFFER SUGGESTED": "#5e35b1",
      "OFFER ACCEPTED": "#3949ab",
      PENDING_PAYMENT: "#f9a825",
      PAID: "#2e7d32",
      COMPLETED: "#1565c0",
      CANCELLED: "#c62828",
      UNPAID: "#ef6c00",
      PARTIAL_PAID: "#f9a825",
      OVERPAID: "#00838f",
      SUSPENDED: "#ef6c00",
      INVITED: "#1565c0",
      INDIVIDUAL: "#1976d2",
      COMPANY: "#7b1fa2",
      Draft: "#607d8b",
      "Offer Suggested": "#5e35b1",
      "Offer Accepted": "#3949ab",
      "Pending Payment": "#f9a825",
      Paid: "#2e7d32",
      Completed: "#1565c0",
      Cancelled: "#c62828",
      Unpaid: "#ef6c00",
      "Partial Paid": "#f9a825",
      Overpaid: "#00838f"
    };
    return semantic[l] ?? PALETTE[i % PALETTE.length];
  });

const ComparisonBarChart: FC<ComparisonBarChartProps> = ({
  title,
  data,
  emptyMessage
}) => {
  const { t } = useKivunovaTranslation();
  const resolvedEmpty =
    emptyMessage ?? t("dashboard-chart-empty", "No data to display");
  const hasData = data.length > 0;
  const labels = data.map((d) => d.label);
  const values = data.map((d) => d.value);

  return (
    <Box sx={{ width: "100%", minHeight: 240 }}>
      {title && (
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      {hasData ? (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <BarChart
            height={Math.max(260, labels.length * 40)}
            xAxis={[
              {
                scaleType: "band",
                data: labels,
                valueFormatter: (v) =>
                  translateDashboardDistributionLabel(String(v), t),
                colorMap: {
                  type: "ordinal",
                  values: labels,
                  colors: getBarColors(labels)
                }
              }
            ]}
            yAxis={[{ tickMinStep: 1 }]}
            series={[
              {
                data: values,
                label: t("dashboard-comparison-series-count", "Count"),
                barLabel: "value"
              }
            ]}
            borderRadius={8}
            margin={{ top: 20, right: 24, bottom: 56, left: 48 }}
            hideLegend
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 180,
            py: 4,
            px: 2
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {resolvedEmpty}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ComparisonBarChart;
